// Fitness vs fatigue, where the gap between the lines IS form: shaded orange
// when fatigue is on top (tired), blue when fitness is (fresh). Labels sit on
// the chart instead of in a legend, and the big moments are called out.
import { useEffect, useRef, useState } from "react";
import { Box, Button, Flex, Text } from "@chakra-ui/react";
import { useInView } from "framer-motion";
import { ACCENT, BG, BLUE, BODY, DIM, FG, GRID, HEAT, MONO, MUTED, alpha } from "../theme";
import { Tooltip, fmtDate, fmtLongDate, hoverProps, linePath, makeScale, smoothPath, useHover } from "../fitness/ui";
import { zoneFor } from "../fitness/model";

const ML = 40;
const MR = 128;
const MT = 34;

const reduced = () => typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const signed = (v) => `${v > 0 ? "+" : v < 0 ? "−" : ""}${Math.abs(Math.round(v))}`;

// `width` is the drawing's own coordinate width: narrower columns pass a
// smaller one so the labels come out bigger on screen.
const FormGap = ({ rows, height = 340, width = 780, replay = false, id = "fg" }) => {
  const W = width;
  const H = height;
  const MB = 34;
  const n = rows.length;
  const sx = makeScale(0, n - 1, ML, W - MR);
  const vals = rows.flatMap((r) => [r.ctl, r.atl]);
  const lo = Math.floor(Math.min(...vals) / 10) * 10 - 4;
  const hi = Math.ceil(Math.max(...vals) / 10) * 10 + 6;
  const sy = makeScale(lo, hi, H - MB, MT);
  const ctl = rows.map((r, i) => [sx(i), sy(r.ctl)]);
  const atl = rows.map((r, i) => [sx(i), sy(r.atl)]);
  const between = `${linePath(ctl)}${linePath([...atl].reverse()).replace("M", "L")}Z`;
  const above = `M${ML},${MT - 20}${linePath(ctl).replace("M", "L")}L${W - MR},${MT - 20}Z`;
  const below = `M${ML},${H - MB}${linePath(ctl).replace("M", "L")}L${W - MR},${H - MB}Z`;
  const [hov, onMove, onLeave] = useHover(n, sx, W);

  // the moments worth pointing at
  const iPeak = rows.reduce((b, r, i) => (r.ctl > rows[b].ctl ? i : b), 0);
  const iTired = rows.reduce((b, r, i) => (r.tsb < rows[b].tsb ? i : b), 0);
  const iFresh = rows.reduce((b, r, i) => (r.tsb > rows[b].tsb ? i : b), 0);
  const last = rows[n - 1];
  const zone = zoneFor(last.tsb);

  // optional draw-in replay
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const [progress, setProgress] = useState(replay ? 0 : 1);
  const raf = useRef(null);
  const play = () => {
    cancelAnimationFrame(raf.current);
    if (reduced()) {
      setProgress(1);
      return;
    }
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.max(0, Math.min(1, (t - t0) / 4200));
      setProgress(1 - (1 - p) ** 2);
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
  };
  useEffect(() => {
    if (replay && inView) play();
    return () => cancelAnimationFrame(raf.current);
  }, [inView, replay]); // eslint-disable-line react-hooks/exhaustive-deps
  const clipW = (W - MR - ML) * progress;
  const done = progress >= 1;

  // month ticks
  const months = [];
  rows.forEach((r, i) => {
    if (i && r.d.slice(0, 7) !== rows[i - 1].d.slice(0, 7)) months.push([i, fmtDate(`${r.d.slice(0, 7)}-01`).split(" ")[0]]);
  });

  // end labels, nudged apart if they collide
  let yF = sy(last.ctl);
  let yT = sy(last.atl);
  if (Math.abs(yF - yT) < 34) {
    const mid = (yF + yT) / 2;
    const s = yF < yT ? -1 : 1;
    yF = mid + s * 17;
    yT = mid - s * 17;
  }

  const callout = (i, label, y, color, up = true) => {
    const x = sx(i);
    const ty = up ? y - 16 : y + 26;
    const anchor = x > W - MR - 120 ? "end" : x < ML + 90 ? "start" : "middle";
    return (
      <g key={label} opacity={done ? 1 : 0} style={{ transition: "opacity .5s" }}>
        <circle cx={x} cy={y} r={5} fill={color} stroke={BG} strokeWidth={2} />
        <text x={x} y={ty} textAnchor={anchor} fill={color} fontSize="11" fontFamily={MONO}>
          {label}
        </text>
      </g>
    );
  };

  return (
    <Box position="relative" ref={ref}>
      {replay && (
        <Flex justify="flex-end" mb={1}>
          <Button size="xs" variant="line" onClick={play}>
            ↻ Replay
          </Button>
        </Flex>
      )}
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" {...hoverProps(onMove, onLeave)} aria-label="Fitness and fatigue over time; the shaded gap between them is form">
        <defs>
          <clipPath id={`${id}-above`}>
            <path d={above} />
          </clipPath>
          <clipPath id={`${id}-below`}>
            <path d={below} />
          </clipPath>
          <clipPath id={`${id}-reveal`}>
            <rect x={0} y={0} width={ML + clipW + 1} height={H} />
          </clipPath>
        </defs>
        {/* quiet horizontal guides */}
        {[lo + (hi - lo) * 0.25, lo + (hi - lo) * 0.5, lo + (hi - lo) * 0.75].map((v) => (
          <g key={v}>
            <line x1={ML} x2={W - MR} y1={sy(v)} y2={sy(v)} stroke={GRID} strokeDasharray="2 5" />
            <text className="tick" x={ML - 8} y={sy(v) + 3.5} textAnchor="end" fill={DIM} fontSize="10" fontFamily={MONO}>
              {Math.round(v)}
            </text>
          </g>
        ))}
        {months.map(([i, m]) => (
          <g key={i}>
            <line x1={sx(i)} x2={sx(i)} y1={H - MB} y2={H - MB + 6} stroke={GRID} />
            <text className="tick" x={sx(i) + 4} y={H - 10} fill={DIM} fontSize="10" fontFamily={MONO}>
              {m}
            </text>
          </g>
        ))}

        <g clipPath={`url(#${id}-reveal)`}>
          <path d={between} fill={alpha(HEAT, 0.26)} clipPath={`url(#${id}-above)`} />
          <path d={between} fill={alpha(BLUE, 0.3)} clipPath={`url(#${id}-below)`} />
          <path d={smoothPath(atl)} fill="none" stroke={HEAT} strokeWidth={1.8} />
          <path d={smoothPath(ctl)} fill="none" stroke={ACCENT} strokeWidth={3} />
        </g>

        {iPeak < n - 10 && callout(iPeak, `peak fitness ${Math.round(rows[iPeak].ctl)} · ${fmtDate(rows[iPeak].d)}`, sy(rows[iPeak].ctl), ACCENT, true)}
        {iTired !== n - 1 && callout(iTired, `most tired ${signed(rows[iTired].tsb)}`, sy(rows[iTired].atl), HEAT, true)}
        {rows[iFresh].tsb > 3 && callout(iFresh, `freshest ${signed(rows[iFresh].tsb)}`, sy(rows[iFresh].ctl), BLUE, false)}

        {/* where things stand today */}
        {done && (
          <g>
            <line x1={W - MR + 10} x2={W - MR + 10} y1={sy(last.ctl)} y2={sy(last.atl)} stroke={zone.color} strokeWidth={2} />
            <line x1={W - MR + 4} x2={W - MR + 10} y1={sy(last.ctl)} y2={sy(last.ctl)} stroke={zone.color} strokeWidth={2} />
            <line x1={W - MR + 4} x2={W - MR + 10} y1={sy(last.atl)} y2={sy(last.atl)} stroke={zone.color} strokeWidth={2} />
            <text x={W - MR + 18} y={yF + 4} fill={ACCENT} fontSize="13" fontWeight="600">
              Fitness {Math.round(last.ctl)}
            </text>
            <text x={W - MR + 18} y={yT + 4} fill={HEAT} fontSize="13" fontWeight="600">
              Fatigue {Math.round(last.atl)}
            </text>
            <text x={W - MR + 18} y={(yF + yT) / 2 + (Math.abs(yF - yT) > 50 ? 4 : 40)} fill={zone.color} fontSize="11" fontFamily={MONO}>
              form {signed(last.tsb)}
            </text>
          </g>
        )}

        {hov !== null && (
          <g>
            <line x1={sx(hov)} x2={sx(hov)} y1={MT - 10} y2={H - MB} stroke={alpha(FG, 0.35)} strokeDasharray="3 3" />
            <circle cx={sx(hov)} cy={sy(rows[hov].ctl)} r={5} fill={ACCENT} stroke={BG} strokeWidth={2} />
            <circle cx={sx(hov)} cy={sy(rows[hov].atl)} r={4} fill={HEAT} stroke={BG} strokeWidth={2} />
          </g>
        )}
      </svg>
      {hov !== null && (
        <Tooltip
          w={W}
          x={sx(hov)}
          title={fmtLongDate(rows[hov].d)}
          rows={[
            ["Fitness", rows[hov].ctl.toFixed(0), ACCENT],
            ["Fatigue", rows[hov].atl.toFixed(0), HEAT],
            [
              rows[hov].tsb < 0 ? `${Math.abs(Math.round(rows[hov].tsb))} more tired than fit` : `fresh by ${Math.round(rows[hov].tsb)}`,
              zoneFor(rows[hov].tsb).name,
              zoneFor(rows[hov].tsb).color,
            ],
          ]}
        />
      )}
      <Flex gap={5} mt={3} wrap="wrap" align="center">
        <Flex align="center" gap={2}>
          <Box w="16px" h="10px" borderRadius="2px" bg={alpha(HEAT, 0.45)} />
          <Text fontSize="xs" color={BODY}>
            fatigue above fitness = <Text as="span" color={HEAT}>tired</Text>
          </Text>
        </Flex>
        <Flex align="center" gap={2}>
          <Box w="16px" h="10px" borderRadius="2px" bg={alpha(BLUE, 0.5)} />
          <Text fontSize="xs" color={BODY}>
            fitness above fatigue = <Text as="span" color={BLUE}>fresh</Text>
          </Text>
        </Flex>
        <Text fontSize="xs" color={MUTED}>the size of the gap is my form</Text>
      </Flex>
    </Box>
  );
};

export default FormGap;
